import React, { useEffect, useMemo, useState } from 'react'
import { Button, Icon } from 'semantic-ui-react'
import { moveDealStage } from 'utility/services/crm'
import { applyPendingMoves, columnNames, groupByStage, resolveDrop } from 'utility/crm/deal-board'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast, useDeals, useForecast, usePreference } from 'hooks/crm'
import { AsyncState, ChipButton, Row } from './ui'
import { DealBoard } from './DealBoard'
import { DealDrawer } from './DealDrawer'
import { DealFormModal } from './DealFormModal'
import { DealList } from './DealList'
import { ForecastStrip } from './ForecastStrip'
import { LostDialog } from './LostDialog'
import { ProposalDialog } from './ProposalDialog'
import { ProposalPrint } from './ProposalPrint'
import { WonDialog } from './WonDialog'

const STATUS_FILTERS = [
    ['', 'Open & won'],
    ['open', 'Open'],
    ['won', 'Won'],
    ['lost', 'Lost'],
]

/**
 * Sales pipeline: forecast strip, board (drag and drop) or list, pipeline switch, stuck-only toggle,
 * deal drawer and the deal / proposal / won / lost dialogs.
 * @param {{ openDealId: number|null, onOpenDeal: (id: number|null) => void, onOpenContact: (id: number) => void }} props
 */
export const PipelineView = ({ openDealId, onOpenDeal, onOpenContact }) => {
    const { lookups } = useCrmLookups()
    const { showError } = useCrmToast()
    const [pipeline, setPipeline] = usePreference('pipeline', 'New business')
    const [mode, setMode] = usePreference('pipelineMode', 'board')
    const [stuckOnly, setStuckOnly] = useState(false)
    const [status, setStatus] = useState('')
    const [pending, setPending] = useState({}) // dealId -> stage, shown before the server confirms
    const [form, setForm] = useState({ open: false, deal: null, defaults: {} })
    const [proposalFor, setProposalFor] = useState(null)
    const [printing, setPrinting] = useState({ deal: null, proposal: null })
    const [wonFor, setWonFor] = useState(null)
    const [lostFor, setLostFor] = useState(null)

    const pipelines = lookups?.Pipelines || ['New business', 'Renewals']
    const query = { pipeline, stuckOnly: stuckOnly || undefined, status: mode === 'list' && status ? status : undefined }
    const deals = useDeals(query)
    const forecast = useForecast(pipeline)

    const stageNames = columnNames(forecast.data, lookups?.Stages)
    const columns = useMemo(
        () => (forecast.data?.Columns?.length ? forecast.data.Columns : (lookups?.Stages || []).map(s => ({ Stage: s.Name, Count: 0, Amount: 0, Probability: s.Probability }))),
        [forecast.data, lookups],
    )
    const allDeals = useMemo(() => applyPendingMoves(deals.data || [], pending), [deals.data, pending])
    const groups = useMemo(() => groupByStage(allDeals, stageNames), [allDeals, stageNames])
    const dealsById = useMemo(() => Object.fromEntries(allDeals.map(d => [d.DealId, d])), [allDeals])

    // Once the refetched deals show the new stage, the optimistic override is no longer needed.
    useEffect(() => {
        if (!deals.data) return
        setPending(prev => {
            const next = { ...prev }
            deals.data.forEach(d => {
                if (next[d.DealId] === d.Stage) delete next[d.DealId]
            })
            return Object.keys(next).length === Object.keys(prev).length ? prev : next
        })
    }, [deals.data])

    const onDrop = async (deal, toStage) => {
        const action = resolveDrop(deal, toStage)
        if (action === 'noop') return
        if (action === 'won') {
            // Never call Stage with Won (400 USE_WON_ENDPOINT): the handoff dialog does it properly.
            setWonFor(deal)
            return
        }
        setPending(p => ({ ...p, [deal.DealId]: toStage })) // optimistic
        try {
            await moveDealStage(deal.DealId, toStage)
            invalidateCrm(...ALL_CRM_SCOPES) // the override is dropped by the effect above once the new stage arrives
        } catch (err) {
            // Revert: remove the override so the card snaps back, and tell the user why.
            setPending(p => {
                const next = { ...p }
                delete next[deal.DealId]
                return next
            })
            showError(err)
            invalidateCrm('deals', 'forecast')
        }
    }

    const openNewDeal = (stage = 'New Lead') => setForm({ open: true, deal: null, defaults: { Stage: stage, Pipeline: pipeline } })
    const listDeals = allDeals

    return (
        <div>
            <Row $between $wrap style={{ marginBottom: 12 }}>
                <Row $gap={10} $wrap>
                    <h3 style={{ margin: 0 }}>Pipeline</h3>
                    <select aria-label="Pipeline" value={pipeline} onChange={e => setPipeline(e.target.value)} style={{ padding: 6 }}>
                        {pipelines.map(p => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </Row>
                <Row $gap={8} $wrap>
                    <Button.Group size="small">
                        <Button type="button" active={mode === 'board'} aria-pressed={mode === 'board'} onClick={() => setMode('board')}>
                            <Icon name="columns" /> Board
                        </Button>
                        <Button type="button" active={mode === 'list'} aria-pressed={mode === 'list'} onClick={() => setMode('list')}>
                            <Icon name="list" /> List
                        </Button>
                    </Button.Group>
                    <Button type="button" primary size="small" onClick={() => openNewDeal()}>
                        <Icon name="plus" /> New deal
                    </Button>
                </Row>
            </Row>

            <ForecastStrip forecast={forecast.data} stuckDays={lookups?.StuckDays || 14} stuckOnly={stuckOnly} onToggleStuck={() => setStuckOnly(s => !s)} />
            {forecast.error && !forecast.data ? <p role="alert">{forecast.error.message}</p> : null}

            {mode === 'list' ? (
                <Row $gap={6} $wrap role="group" aria-label="Deal status" style={{ margin: '14px 0 10px' }}>
                    {STATUS_FILTERS.map(([value, text]) => (
                        <ChipButton key={value || 'default'} type="button" $active={status === value} aria-pressed={status === value} onClick={() => setStatus(value)}>
                            {text}
                        </ChipButton>
                    ))}
                </Row>
            ) : (
                <div style={{ height: 14 }} />
            )}

            <AsyncState
                loading={deals.loading}
                error={deals.error}
                hasData={Boolean(deals.data)}
                empty={Boolean(deals.data) && mode === 'list' && listDeals.length === 0}
                emptyText={stuckOnly ? 'No stuck deals. Nice.' : 'No deals here yet.'}
                onRetry={deals.refetch}
            >
                {mode === 'board' ? (
                    <div aria-busy={deals.loading}>
                        <DealBoard columns={columns} groups={groups} dealsById={dealsById} onDrop={onDrop} onOpen={d => onOpenDeal(d.DealId)} onAdd={openNewDeal} />
                    </div>
                ) : (
                    <DealList deals={listDeals} onOpen={d => onOpenDeal(d.DealId)} />
                )}
            </AsyncState>

            <DealDrawer
                dealId={openDealId}
                onClose={() => onOpenDeal(null)}
                onEdit={deal => setForm({ open: true, deal, defaults: {} })}
                onProposal={setProposalFor}
                onWon={setWonFor}
                onLost={setLostFor}
                onViewProposal={deal => setPrinting({ deal, proposal: deal.Proposal })}
                onOpenContact={onOpenContact}
            />
            <DealFormModal open={form.open} deal={form.deal} defaults={form.defaults} onClose={() => setForm(f => ({ ...f, open: false }))} onSaved={() => {}} />
            <ProposalDialog deal={proposalFor} onClose={() => setProposalFor(null)} onPreview={(deal, proposal) => setPrinting({ deal, proposal })} />
            <ProposalPrint deal={printing.deal} proposal={printing.proposal} onClose={() => setPrinting({ deal: null, proposal: null })} />
            <WonDialog deal={wonFor} onClose={() => setWonFor(null)} />
            <LostDialog deal={lostFor} onClose={() => setLostFor(null)} />
        </div>
    )
}
